import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Container,
    Divider,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface QuestionProps {
    title: string;
    description: string | React.ReactNode;
}

const Question: React.FC = () => {
    const linkStyle = { color: '#062159', textDecoration: 'underline', fontWeight: 600 };

    const facts: QuestionProps[] = [
        {
            title: "Що потрібно для участі?",
            description: (
                <>
                    Заповнити <a href="https://forms.office.com/e/X3yd36cNkk" target="_blank" rel="noreferrer" style={linkStyle}>онлайн-форму</a>, пройти відбір та отримати підтвердження участі.
                </>
            )
        },
        {
            title: "До коли можна заповнити форму реєстрації?",
            description: "Форму реєстрації можна заповнити до 21 грудня 2025 року включно."
        },
        {
            title: "Скільки коштує участь у програмі?",
            description: "Організаційний внесок складає 1950 грн."
        },
        {
            title: "Чи можна проживати на кампусі під час KMA Winter School?",
            description: "Для учасників та учасниць, які мешкають за межами Києва, буде можливість безкоштовного проживання на території Києво-Могилянської академії. Кількість місць обмежена."
        },
        {
            title: 'Що входить до програми?',
            description: 'Навчальні сесії, воркшопи, інтерактивні заходи, виїзний день до компаній-партнерів Могилянки.'
        },
        {
            title: "Що означають напрями KMA Winter School?",
            description: (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography component="span" sx={{ fontFamily: 'Kyiv Type Sans' }}>
                        Кожен учасник та учасниця обирає один із двох напрямів, за яким проходитиме частину навчальних занять:
                    </Typography>

                    <Box>
                        <Typography component="span" sx={{ fontFamily: 'Kyiv Type Sans', fontWeight: 700 }}>
                            🔬 Medical
                        </Typography>
                        <Typography component="span" sx={{ fontFamily: 'Kyiv Type Sans' }}>
                            {' '}– напрям для абітурієнтів, які мріють про вступ на медичний напрям, хочуть побачити справжню роботу лікаря, зрозуміти систему охорони здоров’я та спробувати себе у професійних сценаріях.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography component="span" sx={{ fontFamily: 'Kyiv Type Sans', fontWeight: 700 }}>
                            💸 Financial
                        </Typography>
                        <Typography component="span" sx={{ fontFamily: 'Kyiv Type Sans' }}>
                            {' '}– напрям підійде абітурієнтам та абітурієнткам, які цікавляться інвестиціями, управлінням коштами, роботою банків і сучасними фінансовими інструментами.
                        </Typography>
                    </Box>
                </Box>
            )
        },
        {
            title: 'Чи потрібен дозвіл від батьків?',
            description: 'Так, кожен учасник та учасниця має надати підписану форму згоди батьків або відповідальної особи. Форма буде надіслана разом із листом про підтвердження участі.'
        },
        {
            title: 'Чи передбачене харчування?',
            description: 'Так, учасників та учасниць забезпечать обідами протягом KMA Winter School.'
        }
    ];

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Container id={"question"} maxWidth="lg" sx={{ pb: { xs: 4, sm: 6, md: 8 } }} >
            <Typography
                variant="h4"
                sx={{
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: { xs: 2.5, sm: 4, md: 6 },
                    fontFamily: 'Kyiv Type Serif',
                    lineHeight: 1.2
                }}
            >
                Поширені запитання
            </Typography>

            <Box>
                {facts.map((faq, index) => (
                    <Accordion
                        key={index}
                        sx={{
                            mb: 1.5,
                            boxShadow: 'none',
                            '&:before': {
                                display: 'none',
                            },
                            '& .MuiAccordionSummary-root': {
                                padding: isMobile ? '8px 12px' : '10px 16px',
                                minHeight: isMobile ? 40 : 48,
                                '& .MuiTypography-root': {
                                    fontSize: { xs: 14, sm: 16, md: 18 },
                                    lineHeight: 1.3,
                                },
                            },
                            '& .MuiAccordionDetails-root': {
                                padding: isMobile ? '8px 12px' : '10px 16px',
                                '& .MuiTypography-root': {
                                    fontSize: { xs: 13, sm: 14, md: 16 },
                                    lineHeight: 1.4,
                                },
                            },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel${index}-content`}
                            id={`panel${index}-header`}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontFamily: 'Kyiv Type Sans',
                                    flexGrow: 1,
                                }}
                            >
                                {faq.title}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Divider sx={{ mb: 1 }} />
                            <Box
                                sx={{
                                    fontFamily: 'Kyiv Type Sans',
                                    fontWeight: 400,
                                    textAlign: 'left',
                                    fontSize: { xs: 13, sm: 14, md: 16 },
                                    lineHeight: 1.4,
                                }}
                            >
                                {faq.description}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </Container>
    );
};

export default Question;